import {useState, useRef, useContext} from "react";
import Modal from "react-bootstrap/Modal";
import {source, layer, HtmlMarker, data, Popup} from "azure-maps-control";
import {icon} from "@fortawesome/fontawesome-svg-core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import AuthContext from "context/AuthContext";
import {getFontAwesomeIconHTML} from "components/waste-icons/svgs/getMarkerHTML";
import AzureMapBase from "components/maps/azure/AzureMapBase";
import Loader from "components/loaders/Loader";
import DateTimesUtils from "core/utils/DateTimesUtils";
import WasteSelectorTypes from "components/waste-icons/WasteSelectorTypes";
import getRegularPercentageContainerSVGJS from "components/waste-icons/svgs/getRegularPercentageContainerSVGJS";

const TabPreviewMapTravelAzure = ({show, setShow, assetsIn, assetsOut, travel, pointsModels}) => {
    const {panel} = useContext(AuthContext);
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(true);
    const routeDataSourceRef = useRef(new source.DataSource());
    const markersRef = useRef([]);
    const sharedPopup = useRef(new Popup({pixelOffset: [0, -30]}));

    const getWasteMarkerHTML = (wasteType, percent = null) => {
        const wasteColors = WasteSelectorTypes[wasteType] || {
            fillColor: "#FFE469",
            borderColor: "#D3A204",
            contrastColor: "#F7CA00",
            backgroundColor: "#FFF09F",
        };
        return getRegularPercentageContainerSVGJS({
            ...wasteColors,
            percent,
            width: 57.41,
            height: 80.21,
            overflow: percent > 100,
        });
    };

    const getFontAwesomeMarkerHTML = (iconClass) => {
        let iconSVG = icon({prefix: "fas", iconName: iconClass});
        if (!iconSVG || !iconSVG.html || !iconSVG.html.length) {
            iconSVG = icon({prefix: "far", iconName: "question"});
        }
        const iconHTML = iconSVG.html.join("");
        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="60" viewBox="0 0 57.41 80.21">
                <g transform="translate(0 0)">
                    <path d="M 27.74440765380859 79.32769012451172 C 25.11689186096191 75.6302490234375 11.02597236633301 55.75102996826172 8.177006721496582 50.67843627929688 C 5.519217491149902 46.01403427124023 3.804895401000977 42.46542358398438 2.616940021514893 39.16965866088867 C 1.192462205886841 35.21771240234375 0.5000066161155701 31.48502540588379 0.5000066161155701 27.75830268859863 C 20.5085620880127 1.220636010169983 24.07829475402832 0.5000026822090149 27.75837326049805 0.5000026822090149 C 31.43845176696777 0.5000026822090149 35.00818252563477 1.220636010169983 38.368408203125 2.641891479492188 C 54.29611587524414 20.50851440429688 55.01675033569336 24.0782356262207 55.01675033569336 27.75830268859863 C 55.01675033569336 31.96585845947266 54.32773971557617 35.81558227539062 52.91033935546875 39.52744674682617 C 42.18861770629883 57.56851577758789 30.10642433166504 75.77255249023438 27.74440765380859 79.32769012451172 Z" fill="#e2e2e2"></path>
                    <foreignObject x="9" y="10" width="40" height="40">
                        <div xmlns="http://www.w3.org/1999/xhtml" style="font-size: 28px; color: gray; text-align: center;">
                            ${iconHTML}
                        </div>
                    </foreignObject>
                </g>
            </svg>
        `;
    };

    const onMapReady = (mapInstance) => {
        setMap(mapInstance);

        mapInstance.sources.add(routeDataSourceRef.current);

        const bubbleLayer = new layer.BubbleLayer(routeDataSourceRef.current, null, {
            // Importante: Filtramos para que esta capa SOLO pinte los Puntos
            filter: ["any", ["==", ["geometry-type"], "Point"], ["==", ["geometry-type"], "MultiPoint"]],
            radius: 12,
            color: "#6342c3",
            opacity: 0.5,
            strokeColor: "#8160dc",
            strokeWidth: 1,
        });
        mapInstance.layers.add(bubbleLayer);

        // 3. Tu capa de líneas existente
        const lineLayer = new layer.LineLayer(routeDataSourceRef.current, null, {
            // Filtramos para que esta capa SOLO pinte las Líneas
            filter: ["any", ["==", ["geometry-type"], "LineString"], ["==", ["geometry-type"], "MultiLineString"]],
            strokeColor: ["coalesce", ["get", "strokeColor"], "#3388ff"],
            strokeWidth: 2,
            strokeOpacity: 0.8,
        });
        mapInstance.layers.add(lineLayer);

        setTimeout(() => {
            addAssetsMap(mapInstance);
            if (travel.current) {
                paintRouteAll(travel.current, mapInstance);
            }
            setLoading(false);
        }, 500);
    };

    const clearMarkers = () => {
        markersRef.current.forEach((item) => {
            // 1. El item contiene { marker, id } según tu código
            const mk = item.marker;

            // 2. Eliminar eventos para evitar fugas de memoria
            map.events.remove("click", mk);

            // 3. Quitar el marcador del mapa
            if (map && map.markers) {
                map.markers.remove(mk);
            }
        });

        // 4. Vaciar la referencia
        markersRef.current = [];
    };

    const addAssetsMap = (mapInstance) => {
        if (!mapInstance) return;

        // Clear previous markers
        clearMarkers();
        // Suponiendo que sharedPopup vive en un nivel superior
        if (sharedPopup.current) sharedPopup.current.close();
        // points to setCamera bbox
        const points = [];
        // Add Include Assets
        assetsIn.forEach((way) => {
            const asset = way.asset;
            const html = getWasteMarkerHTML(asset.waste_type, asset.filling);
            const position = [asset.lng, asset.lat];
            points.push(position);

            const marker = new HtmlMarker({
                htmlContent: html,
                position: [asset.lng, asset.lat],
                pixelOffset: [0, 0],
            });

            // Evento con el popup compartido
            mapInstance.events.add("click", marker, () => {
                const popupContent = `UID: ${asset.uid}`;
                sharedPopup.current.setOptions({
                    content: `<div style="padding:10px; color: black;">${popupContent}</div>`,
                    position: position,
                });
                sharedPopup.current.open(mapInstance);
            });

            if (mapInstance.markers) mapInstance.markers.add(marker);
            markersRef.current.push({marker, id: asset.id});
        });

        // Add Exclude Assets
        // if (assetsOut && assetsOut.length > 0) {
        //     assetsOut.forEach((way) => {
        //         if (way.asset) {
        //             const asset = way.asset;
        //             const html = getWasteMarkerHTML(asset.waste_type, asset.filling);
        //             const marker = new HtmlMarker({
        //                 htmlContent: `<div style="opacity: 0.6">${html}</div>`,
        //                 position: [asset.lng, asset.lat],
        //                 pixelOffset: [0, -30],
        //             });
        //             mapInstance.markers.add(marker);
        //             markersRef.current.push(marker);
        //         }
        //     });
        // }
        const POINTS = ["refueling", "unload", "origin", "destination"];
        // Add model points (Origin, Destination, etc.)
        Object.keys(pointsModels.current).forEach((key) => {
            if (POINTS.includes(key)) {
                const point = pointsModels.current[key];
                if (point && point.lat && point.lng) {
                    //const html = getFontAwesomeMarkerHTML(point.icon);
                    const html = getFontAwesomeIconHTML(point.icon);
                    const marker = new HtmlMarker({
                        htmlContent: html,
                        position: [point.lng, point.lat],
                        pixelOffset: [0, 0],
                    });
                    points.push([point.lng, point.lat]);
                    mapInstance.markers.add(marker);
                    markersRef.current.push(marker);
                }
            }
        });

        // 3. Ajuste de cámara (FitBounds)
        if (points.length > 0) {
            mapInstance.setCamera({
                // Ojo: verifica si es 'atlas' o 'atlasData' según tu importación
                bounds: data.BoundingBox.fromPositions(points),
                padding: 50,
                type: "jump",
            });
        }
    };

    const paintRouteAll = (_travel, mapInstance = map) => {
        if (!mapInstance || !routeDataSourceRef.current || !_travel) return;

        routeDataSourceRef.current.clear();

        const coordinates = _travel.geometry.coordinates;
        const allCoords = coordinates.flat();

        // Azure expects [lng, lat]
        const line = new data.LineString(allCoords);
        routeDataSourceRef.current.add(new data.Feature(line, {strokeColor: "#3388ff"}));

        const bounds = data.BoundingBox.fromData(line);
        mapInstance.setCamera({
            bounds: bounds,
            padding: 50,
        });
    };

    const paintRouteStep = async (_travel) => {
        if (!map || !routeDataSourceRef.current || !_travel) return;
        routeDataSourceRef.current.clear();

        const accumulatedCoords = [];
        // Guardaremos los puntos (circles) en un array aparte para no perderlos
        const stepPoints = [];

        for (const step of _travel.geometry.coordinates) {
            for (const coord of step) {
                accumulatedCoords.push(coord);

                if (accumulatedCoords.length >= 2) {
                    const line = new data.Feature(new data.LineString(accumulatedCoords), {
                        strokeColor: "green",
                        strokeWidth: 2,
                        opacity: 0.5,
                    });

                    // Seteamos la línea actual + todos los puntos acumulados
                    routeDataSourceRef.current.setShapes([line, ...stepPoints]);
                }
                await DateTimesUtils.sleep(100);
            }

            // Al terminar el step, guardamos el punto permanentemente
            const lastCoord = step.at(-1);
            if (lastCoord) {
                const pointFeature = new data.Feature(new data.Point(lastCoord));
                stepPoints.push(pointFeature);

                // Actualizamos una última vez para que aparezca el círculo recién añadido
                const line = new data.Feature(new data.LineString(accumulatedCoords), {strokeColor: "green"});
                routeDataSourceRef.current.setShapes([line, ...stepPoints]);
            }
        }
    };

    const handlePlay = async () => {
        await paintRouteStep(travel.current);
        paintRouteAll(travel.current);
    };
    // <button type="button" className="btn btn-sm btn-outline-secondary ms-3" onClick={handlePlay}>
    //     <FontAwesomeIcon icon="play" /> Ruta
    // </button>;
    return (
        <Modal show={show} fullscreen={true} onHide={() => setShow(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Mapa ruta dinamica (Azure)</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{padding: 0}}>
                <div style={{position: "relative", height: "100%", width: "100%"}}>
                    <AzureMapBase center={panel.coords} zoom={13} height="calc(100vh - 90px)" onMapReady={onMapReady} />
                    <Loader show={loading} />
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default TabPreviewMapTravelAzure;
